import { api, send } from "../api";
import type { Alarms, Delivery } from "../types";
import { actionButton, ago, dateTime, detailBox, flash, h, link, nodeLink, show, statusChip, table, td, th } from "../ui";

/** "push 2 sent · email ok · bark skipped" from POST /alarms/test. */
export const deliveryLine = (d: Delivery) =>
  [
    `push ${typeof d.push === "string" ? d.push : `${d.push.sent} sent${d.push.failures.length ? `, ${d.push.failures.length} failed (${d.push.failures.join("; ")})` : ""}`}`,
    `email ${d.email}`,
    `bark ${d.bark}`,
  ].join(" · ");

export const testAlarmButton = () =>
  actionButton("Send me a test alarm", async () => {
    const d = await send<Delivery>("POST", "/v1/admin/alarms/test");
    flash(`Test alarm sent to you: ${deliveryLine(d)}.`);
  });

export async function alarmsPage() {
  const a = await api<Alarms>("/v1/admin/alarms");
  if (!a) return;
  const firing = a.states.filter((s) => s.state === "firing");
  show(
    "Alarms",
    "Checked every 5 minutes. A change (firing, cleared) is logged and sent to owners and admins by push, email and Bark. Set your Bark key on the Me page.",
    h("div", { class: "actions" }, testAlarmButton(), h("span", { class: "muted small" }, "to you only, by every channel you have")),
    h("h2", null, firing.length ? `Firing (${firing.length})` : "Nothing is firing"),
    table(
      [th("Key"), th("Rule"), th("Node"), th("State"), th("Since", "num"), th("Last sent", "num"), th("Detail")],
      a.states.map((s) =>
        h(
          "tr",
          null,
          td(s.key, "mono"),
          td(s.rule),
          h("td", null, s.relay ? nodeLink(s.relay, `${s.relay}${s.label ? ` · ${s.label}` : ""}`) : "—"),
          h("td", null, statusChip(s.state)),
          h("td", { class: "num", title: dateTime(s.since) }, ago(s.since)),
          td(s.last_sent ? dateTime(s.last_sent) : "—", "num"),
          h("td", null, detailBox(s.detail)),
        ),
      ),
      "No alarm has ever fired.",
    ),
    h("h2", null, "Rules"),
    table(
      [th("Rule"), th("Level"), th("Fires"), th("What", "wrap")],
      a.rules.map((r) => h("tr", null, td(r.id, "mono"), h("td", null, statusChip(r.level)), td(r.once ? "once per cycle" : "and clears"), td(r.description, "wrap"))),
      "",
    ),
    h("h2", null, "Last 30 days ", link("/logs?kind=alarm.*", "in the log →")),
    table(
      [th("When", "num"), th("Event"), th("Level"), th("Node"), th("Message", "wrap"), th("Detail")],
      a.history.map((e) => h("tr", null, td(dateTime(e.t), "num"), td(e.kind.replace("alarm.", ""), "mono"), h("td", null, statusChip(e.level)), h("td", null, e.relay ? nodeLink(e.relay) : "—"), td(e.message, "wrap"), h("td", null, detailBox(e.detail)))),
      "Quiet: no alarm fired or cleared in the last 30 days.",
    ),
  );
}
